// airspace-helpers.ts — shared client-side helpers for the airspace
// authorization workflow (pilot dashboard, event widget, producer advisory).
//
// Most of these are pure functions — color/label maps, URL templates — but a
// few do real network IO directly to FAA UASFM and OpenAIP. Those endpoints
// are public, CORS-enabled, and key-less, so we hit them from the browser to
// keep Heroku off the path.

export type AirspaceStatus =
	| "needs_review"
	| "needs_authorization"
	| "pending"
	| "complete"
	| "needs_action";

export type ReferenceStatus =
	| "not_submitted"
	| "pending"
	| "submitted"
	| "in_review"
	| "approved"
	| "denied";

export type ReferenceType =
	| "airspace_authorization"
	| "altitude_waiver"
	| "tfr_waiver"
	| "rain_date_authorization"
	| "reapply_higher";

// ---------------------------------------------------------------------------
// Status presentation
// ---------------------------------------------------------------------------

export const AIRSPACE_STATUS_LABELS: Record<AirspaceStatus, string> = {
	needs_review: "Needs Review",
	needs_authorization: "Needs Auth",
	pending: "Pending",
	complete: "Complete",
	needs_action: "Needs Action",
};

// Compact labels for table columns and dense surfaces — match the column-width
// budget of the existing event status pills ("confirmed", "packing", etc).
export const AIRSPACE_STATUS_LABELS_SHORT: Record<AirspaceStatus, string> = {
	needs_review: "Review",
	needs_authorization: "Auth",
	pending: "Pending",
	complete: "Complete",
	needs_action: "Action!",
};

// Mirror the events-list status pill: ring-1 ring-inset, soft tinted bg, capitalize-cased text.
// Tailwind requires full class strings (not concatenated) for tree-shake.
export const AIRSPACE_STATUS_PILL_CLASSES: Record<AirspaceStatus, string> = {
	needs_review: "bg-[rgb(255_255_255/0.06)]    text-on-surface-variant    ring-[rgb(255_255_255/0.12)]",
	needs_authorization: "bg-amber-500/10   text-amber-400   ring-amber-500/30",
	pending: "bg-sky-500/10     text-sky-400     ring-sky-500/30",
	complete: "bg-emerald-500/10 text-emerald-400 ring-emerald-500/30",
	needs_action: "bg-red-500/15     text-red-300     ring-red-500/40",
};

export const REFERENCE_STATUS_LABELS: Record<ReferenceStatus, string> = {
	not_submitted: "Not Submitted",
	pending: "Pending",
	submitted: "Submitted",
	in_review: "In Review",
	approved: "Approved",
	denied: "Denied",
};

// Pill colors per reference status. The lifecycle reads as a color gradient:
// gray (idle) → amber (pilot prep) → orange (sent) → sky (FAA reviewing) →
// emerald (approved) | red (denied).
export const REFERENCE_STATUS_PILL_CLASSES: Record<ReferenceStatus, string> = {
	not_submitted: "bg-gray-700/60    text-on-surface",
	pending: "bg-amber-500/20   text-amber-200",
	submitted: "bg-orange-500/20  text-orange-200",
	in_review: "bg-sky-500/20     text-sky-200",
	approved: "bg-emerald-500/20 text-emerald-200",
	denied: "bg-red-500/25     text-red-100",
};

export const REFERENCE_TYPE_LABELS: Record<ReferenceType, string> = {
	airspace_authorization: "Airspace Authorization",
	altitude_waiver: "Altitude Waiver",
	tfr_waiver: "TFR Waiver",
	rain_date_authorization: "Rain Date Authorization",
	reapply_higher: "Re-apply (Higher)",
};

// ---------------------------------------------------------------------------
// External-link helpers (zero auth, just URL templates)
// ---------------------------------------------------------------------------

export function skyvectorUrl(lat: number, lng: number): string {
	return `https://skyvector.com/?ll=${lat},${lng}`;
}

export function googleMapsUrl(lat: number, lng: number): string {
	return `https://www.google.com/maps/?q=${lat},${lng}`;
}

export function faaB4UFlyUrl(lat: number, lng: number): string {
	return `https://b4ufly.aloft.ai/?lat=${lat}&lng=${lng}`;
}

// LAANC submission portal (Aloft is the most-used LAANC provider for Part 107 ops)
export function aloftLaancUrl(lat: number, lng: number): string {
	return `https://www.aloft.ai/services/laanc/?lat=${lat}&lng=${lng}`;
}

// FAA DroneZone — where Part 107 waivers (altitude, night, BVLOS, OOP) are submitted.
// Deep-link to the operations dashboard once logged in.
export const FAA_DRONEZONE_URL = "https://faadronezone.faa.gov/";
export const FAA_PART107_WAIVER_URL =
	"https://faadronezone.faa.gov/#/operations";

// FAA's official NOTAM search (graphical + text)
export function faaNotamSearchUrl(lat: number, lng: number): string {
	return `https://notams.aim.faa.gov/notamSearch/?lat=${lat}&lng=${lng}`;
}

// FAA TFR list (Temporary Flight Restrictions — public sport / VIP / events)
export const FAA_TFR_URL = "https://tfr.faa.gov/tfr2/list.html";

// ---------------------------------------------------------------------------
// UASFM — direct browser fetch (public, CORS-enabled, no auth)
// ---------------------------------------------------------------------------

export interface UASFMResult {
	max_ceiling_ft: number | null; // null when no UASFM cell intersects
	controlling_facility: string | null; // ICAO like 'KATL'
	facility_name: string | null; // e.g. 'Hartsfield/Jackson Atlanta Intl'
	airspace_class: string | null; // e.g. 'B', 'C', 'D', 'E'
	cells: Array<{
		ceiling_ft: number | null;
		oid: number;
		airport: string | null;
		class: string | null;
	}>;
	source: "uasfm";
	fetched_at: string;
}

/**
 * FAA UASFM REST endpoint. Verified live against the FAA's published service —
 * 378k+ feature polygons covering UASFM-eligible airspace around towered
 * airports. Override via `REACT_APP_UASFM_URL` if FAA rotates the service path.
 *
 * Service: FAA_UAS_FacilityMap_Data (single layer, ID 0, polygon geometry).
 * The schema covers up to 5 airports per cell (APT1_*..APT5_*) when cells
 * overlap multiple facilities, plus AIRSPACE_1..AIRSPACE_5 for class strings.
 */
const UASFM_DEFAULT_URL =
	"https://services6.arcgis.com/ssFJjBXIUyZDrSYZ/arcgis/rest/services/" +
	"FAA_UAS_FacilityMap_Data/FeatureServer/0/query";

/**
 * Query the FAA UAS Facility Map by point.
 *
 * Verified field list (live against FAA service, May 2026):
 *   CEILING       (integer, ft AGL — `0` means no-fly cell)
 *   APT1_FAAID    (string, primary airport FAA ID)
 *   APT1_ICAO     (string, primary airport ICAO — e.g. 'KATL')
 *   APT1_NAME     (string, primary airport human name)
 *   AIRSPACE_1    (string, airspace class — 'B' | 'C' | 'D' | etc.)
 *   APT2..APT5    (same pattern, present when a cell overlaps multiple facilities)
 *
 * Returns `null` for ceiling/facility/class only when the service returns 0
 * features (genuinely Class G — outside any UASFM grid). Throws on any
 * network/HTTP/Esri error so callers can show "data unavailable".
 */
export async function fetchUASFM(
	lat: number,
	lng: number,
): Promise<UASFMResult> {
	const base = process.env.REACT_APP_UASFM_URL || UASFM_DEFAULT_URL;
	const url = new URL(base);
	url.searchParams.set("f", "json");
	url.searchParams.set("geometryType", "esriGeometryPoint");
	url.searchParams.set("geometry", `${lng},${lat}`); // shorthand: x,y == lng,lat
	url.searchParams.set("inSR", "4326");
	url.searchParams.set("spatialRel", "esriSpatialRelIntersects");
	url.searchParams.set(
		"outFields",
		"CEILING,APT1_FAAID,APT1_ICAO,APT1_NAME,AIRSPACE_1,OBJECTID",
	);
	url.searchParams.set("returnGeometry", "false");

	const finalUrl = url.toString();
	let r: Response;
	try {
		r = await fetch(finalUrl);
	} catch (err: any) {
		const e = new Error(
			`UASFM network error: ${err?.message || "fetch failed"}`,
		);
		(e as any).attempted = finalUrl;
		throw e;
	}
	if (!r.ok) {
		const e = new Error(`UASFM HTTP ${r.status}`);
		(e as any).attempted = finalUrl;
		throw e;
	}
	const data = await r.json();
	if (data?.error) {
		const e = new Error(`UASFM ${data.error.code}: ${data.error.message}`);
		(e as any).attempted = finalUrl;
		(e as any).esriError = data.error;
		throw e;
	}
	if (!Array.isArray(data?.features)) {
		const e = new Error("UASFM malformed response (no features array)");
		(e as any).attempted = finalUrl;
		throw e;
	}

	const cells: UASFMResult["cells"] = [];
	let maxCeiling: number | null = null;
	let facility: string | null = null;
	let facilityName: string | null = null;
	let airspaceClass: string | null = null;

	for (const f of data.features) {
		const a = f.attributes || {};
		const ceiling = a.CEILING != null ? Number(a.CEILING) : null;
		cells.push({
			ceiling_ft: ceiling,
			oid: a.OBJECTID,
			airport: a.APT1_ICAO || a.APT1_FAAID || null,
			class: a.AIRSPACE_1 || null,
		});
		if (ceiling != null && (maxCeiling == null || ceiling > maxCeiling)) {
			maxCeiling = ceiling;
		}
		if (!facility) facility = a.APT1_ICAO || a.APT1_FAAID || null;
		if (!facilityName) facilityName = a.APT1_NAME || null;
		if (!airspaceClass) airspaceClass = a.AIRSPACE_1 || null;
	}

	return {
		max_ceiling_ft: maxCeiling,
		controlling_facility: facility,
		facility_name: facilityName,
		airspace_class: airspaceClass,
		cells,
		source: "uasfm",
		fetched_at: new Date().toISOString(),
	};
}

// ---------------------------------------------------------------------------
// OpenAIP — direct browser fetch for class B/C/D/E polygons (no auth, public)
// ---------------------------------------------------------------------------

// Minimal GeoJSON shape — avoid pulling in @types/geojson for one usage.
export interface GeoJsonPolygon {
	type: "Polygon";
	coordinates: number[][][];
}
export interface GeoJsonMultiPolygon {
	type: "MultiPolygon";
	coordinates: number[][][][];
}

export interface OpenAIPClass {
	/** Single-letter ICAO class for color coding: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'R' | 'P' | 'OTHER' */
	type: string;
	name: string;
	ceiling_ft?: number;
	floor_ft?: number;
	geometry: GeoJsonPolygon | GeoJsonMultiPolygon;
}

// OpenAIP's icaoClass enum. Their API returns this as a numeric code (or a
// letter on some endpoint versions). 7 = SUA (Special Use Airspace).
const ICAO_CLASS_BY_CODE: Record<number, string> = {
	0: "A",
	1: "B",
	2: "C",
	3: "D",
	4: "E",
	5: "F",
	6: "G",
	7: "SUA",
};

// OpenAIP's `type` enum overrides icaoClass for restricted/prohibited blocks.
// We surface these because pilots need to see them on the map even if the
// underlying airspace is "Class E" by ICAO definition.
const TYPE_OVERRIDES_BY_CODE: Record<number, string> = {
	1: "R", // RESTRICTED
	2: "D2", // DANGER
	3: "P", // PROHIBITED
};

function deriveAirspaceClass(item: any): string {
	// Prefer letter form if the API returns one
	if (typeof item.icaoClass === "string" && item.icaoClass.length <= 4)
		return item.icaoClass.toUpperCase();
	if (
		typeof item.icaoClass === "number" &&
		ICAO_CLASS_BY_CODE[item.icaoClass]
	) {
		// But if `type` flags this as Restricted/Prohibited, that wins
		if (
			typeof item.type === "number" &&
			TYPE_OVERRIDES_BY_CODE[item.type]
		) {
			return TYPE_OVERRIDES_BY_CODE[item.type];
		}
		return ICAO_CLASS_BY_CODE[item.icaoClass];
	}
	if (typeof item.type === "number" && TYPE_OVERRIDES_BY_CODE[item.type]) {
		return TYPE_OVERRIDES_BY_CODE[item.type];
	}
	if (typeof item.type === "string") return item.type.toUpperCase();
	return "OTHER";
}

/**
 * Fetch class polygons from OpenAIP within a bounding box.
 *
 * Requires an OpenAIP API key (free — register at openaip.net → My OpenAIP →
 * API Clients). Set as `REACT_APP_OPENAIP_API_KEY` in Vercel project env vars
 * (or a local `.env.local`). The key is read-only and rate-limited per-key,
 * so embedding it in the bundle is acceptable (same model as Mapbox / Stripe
 * publishable keys).
 *
 * Returns [] when the key is missing rather than throwing — the map still
 * renders launch + audience + base tiles, just without class polygons.
 */
export async function fetchOpenAIP(bbox: {
	minLat: number;
	minLng: number;
	maxLat: number;
	maxLng: number;
}): Promise<OpenAIPClass[]> {
	const apiKey = process.env.REACT_APP_OPENAIP_API_KEY;
	if (!apiKey) {
		// eslint-disable-next-line no-console
		console.warn(
			"[OpenAIP] REACT_APP_OPENAIP_API_KEY is not set — class polygons will not load",
		);
		return [];
	}
	// bbox order required by OpenAIP: [west, south, east, north]
	const bboxStr = [bbox.minLng, bbox.minLat, bbox.maxLng, bbox.maxLat].join(
		",",
	);
	const url = `https://api.core.openaip.net/api/airspaces?bbox=${bboxStr}&limit=200&apiKey=${encodeURIComponent(apiKey)}`;
	const r = await fetch(url, {
		headers: {
			Accept: "application/json",
			"x-openaip-api-key": apiKey,
		},
	});
	if (!r.ok) throw new Error(`OpenAIP HTTP ${r.status}`);
	const data = await r.json();
	const items = Array.isArray(data?.items) ? data.items : [];
	return items.map(
		(it: any): OpenAIPClass => ({
			type: deriveAirspaceClass(it),
			name: it.name || "",
			ceiling_ft: it.upperLimit?.value,
			floor_ft: it.lowerLimit?.value,
			geometry: it.geometry,
		}),
	);
}

// ---------------------------------------------------------------------------
// NOTAM — same-origin call to the Vercel function /api/notam
// ---------------------------------------------------------------------------

export interface NotamItem {
	number: string;
	effective_start: string;
	effective_end: string;
	text: string;
	icao_location?: string;
	raw?: unknown;
}

export async function fetchNotams(
	lat: number,
	lng: number,
	opts: {
		effectiveStart?: string;
		effectiveEnd?: string;
		radiusNm?: number;
	} = {},
): Promise<NotamItem[]> {
	const url = new URL("/api/notam", window.location.origin);
	url.searchParams.set("lat", String(lat));
	url.searchParams.set("lng", String(lng));
	if (opts.radiusNm) url.searchParams.set("radius_nm", String(opts.radiusNm));
	if (opts.effectiveStart)
		url.searchParams.set("effective_start", opts.effectiveStart);
	if (opts.effectiveEnd)
		url.searchParams.set("effective_end", opts.effectiveEnd);

	const r = await fetch(url.toString(), { credentials: "include" });

	// Detect the local-dev fallthrough where /api/notam wasn't intercepted and
	// CRA returned the SPA's index.html. The raw "Unexpected token '<'" error
	// is opaque — turn it into something actionable.
	const ct = r.headers.get("content-type") || "";
	if (!ct.includes("json")) {
		if (!r.ok) throw new Error(`NOTAM HTTP ${r.status}`);
		throw new Error(
			"NOTAM proxy not reachable. Local dev needs FAA_NOTAM_API_KEY in .env.local + a dev-server restart; production needs the same key set in Vercel.",
		);
	}

	const data = await r.json();
	if (!r.ok) {
		throw new Error(data?.error || `NOTAM HTTP ${r.status}`);
	}
	// FAA returns { items: [{ properties: { coreNOTAMData: { notam: { ... } } } }, ...] }
	const items = Array.isArray(data?.items) ? data.items : [];
	return items.map((it: any): NotamItem => {
		const n = it?.properties?.coreNOTAMData?.notam || it?.notam || {};
		return {
			number: n.number || n.notamNumber || "",
			effective_start: n.effectiveStart || n.startDate || "",
			effective_end: n.effectiveEnd || n.endDate || "",
			text: n.text || n.icaoMessage || "",
			icao_location: n.icaoLocation,
			raw: n,
		};
	});
}

// ---------------------------------------------------------------------------
// Advisory derivation — pure function combining UASFM + NOTAM into a
// "green / amber / red" hint for the producer's EventForm.
// ---------------------------------------------------------------------------

export type AdvisoryLevel = "green" | "amber" | "red";

export interface AdvisoryResult {
	level: AdvisoryLevel;
	headline: string;
	detail: string;
}

export function deriveAdvisory(
	uasfm: UASFMResult | null,
	notams: NotamItem[],
): AdvisoryResult {
	if (!uasfm) {
		return {
			level: "amber",
			headline: "Airspace data unavailable",
			detail: "Could not reach the FAA UASFM service. Pilot review required.",
		};
	}

	const ceiling = uasfm.max_ceiling_ft;
	const facility = uasfm.facility_name || uasfm.controlling_facility || null;
	const klass = uasfm.airspace_class;
	const hasNotam = notams.length > 0;

	// Outside any UASFM cell == uncontrolled (Class G)
	if (ceiling == null) {
		if (hasNotam) {
			return {
				level: "amber",
				headline: "Open airspace, but active NOTAMs",
				detail: `${notams.length} NOTAM(s) active in 5nm radius. Pilot review recommended.`,
			};
		}
		return {
			level: "green",
			headline: "Open airspace — auth unlikely needed",
			detail: "No UASFM cell intersects this point (likely Class G).",
		};
	}

	// CEILING == 0 means "no-fly without LAANC" — usually Class B/C/D core
	if (ceiling === 0) {
		return {
			level: "red",
			headline: `Class ${klass || "?"} — UASFM ceiling 0 ft`,
			detail: `${facility ? `${facility} controls this point. ` : ""}LAANC airspace authorization required for any flight.${hasNotam ? ` ${notams.length} active NOTAM(s).` : ""}`,
		};
	}

	// 400ft+ ceiling — typically open within Part 107 limits
	if (ceiling >= 400) {
		return {
			level: hasNotam ? "amber" : "green",
			headline: `Class ${klass || "?"} — ceiling ${ceiling} ft`,
			detail: `${facility ? `${facility} controls. ` : ""}LAANC ceiling ${ceiling} ft covers the standard 400 ft Part 107 limit.${hasNotam ? ` ${notams.length} active NOTAM(s).` : ""}`,
		};
	}

	// 100–399 ft — altitude waiver likely needed for typical drone shows
	return {
		level: "amber",
		headline: `Class ${klass || "?"} — ceiling ${ceiling} ft`,
		detail: `${facility ? `${facility} controls. ` : ""}Likely altitude waiver needed for shows above ${ceiling} ft.${hasNotam ? ` ${notams.length} active NOTAM(s).` : ""}`,
	};
}

// ---------------------------------------------------------------------------
// Initials — match the packing-checklist convention
// ---------------------------------------------------------------------------

export function userInitials(
	firstname?: string | null,
	lastname?: string | null,
): string {
	const f = (firstname || "").trim();
	const l = (lastname || "").trim();
	if (!f && !l) return "—";
	return `${f.charAt(0).toUpperCase()}${l.charAt(0).toUpperCase()}`;
}
